'use client'
import React, { useState, useEffect, useCallback } from 'react'
import {PaymentElement, useStripe, useElements, Elements} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure'
import { Button } from "@heroui/react"

import Loading from './loading'
import StatusChip from './status-chip'
import NoCookies from './no-cookies'

import { formatMoneyIntl } from '../src/formatter'
import localStorage from '../src/local-storage'
import { debounce } from '../src/debounce'
import { logEvent } from '../src/analytics'

let stripeKey = 'pk_live_Qm9MFNIAEXmbQ7dlaDkVMm32'
if (process.env.DEV) {
  stripeKey = 'pk_test_Y7EVXu9oDtkGvbxZyxA6ZerE'
}

const Purchase = ({ user, type, currency, quantity, amount, challengeId, updateAmt, cb, validate }) => {
  const [intent, setIntent] = useState(false)
  const [gettingIntent, setGettingIntent] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [stripePromise, setStripePromise] = useState()

  const debounceGetIntent = useCallback(
    debounce((a, b, c, d) => getIntent(a, b, c, d), 500),
    []
  )

  useEffect(() => {
    if (intent && currency !== intent.currency) setLoading(true)
    if (quantity && currency) {
      if (user && user.id && user.cookies && !stripePromise) {
        setStripePromise(loadStripe(stripeKey))
      }
      debounceGetIntent(type, quantity, currency, amount)
    }
  }, [quantity, currency, amount])

  const reset = () => {
    setIntent(false)
    localStorage.removePaymentIntent(type)
    setTimeout(() => {
      getIntent(type, quantity, currency)
    }, 250)
  }

  const getIntent = async (t, q, curr, amt) => {
    if (user && user.id && !user.cookies) return
    setError('')
    setGettingIntent(true)
    let pi = localStorage.getPaymentIntent(type)
    let method
    if (pi && pi.id && pi.clientSecret && pi.userId !== user.id) {
      // diff user, remove PI
      setIntent(false)
      localStorage.removePaymentIntent(type)
      pi = undefined
      method = 'POST'
    }
    if (pi && pi.id && pi.clientSecret && pi.challengeId !== challengeId) {
      // diff challenge, get new PI
      setIntent(false)
      localStorage.removePaymentIntent(type)
      pi = undefined
      method = 'POST'
    }
    if (!pi || !pi.id) {
      // create new  
      method = 'POST'
    }
    if (pi && pi.id && pi.clientSecret && (q !== pi.quantity || curr !== pi.currency)) {
      // changed, need to update PI
      method = 'PUT'
    }
    if (t && pi && (t === 'entryFee' || t === 'donation') && amt !== pi.amount) {
      method = 'PUT'
    }

    if (!method) {
      // use localstorage pi
      setIntent(pi)
      setGettingIntent(false)
      setLoading(false)
      if (updateAmt) updateAmt(pi.amount)
      return pi
    }
    
    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': user.token
      },
      body: JSON.stringify({
        id: pi && pi.id,
        currency: curr,
        type,
        quantity: q,
        challengeId,
        amount: amt
      })
    }
    try {
      const res = await fetch(`${process.env.API}/stripe/intent`, config)
      if (!res.ok) {
        const err = await res.text()
        setError(`Problem retrieving payment intent.. ${err}`)
        setGettingIntent(false)
      } else {
        const data = await res.json()
        localStorage.setPaymentIntent(type, data)
        setIntent(data)
        if (updateAmt) updateAmt(data.amount)
        setGettingIntent(false)
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
      setGettingIntent(false)
      setError(`Problem retrieving payment intent. ${err}`)
    }
  }
  
  if (user && user.id && !user.cookies) return <NoCookies desc='payments are unavailable without cookies' /> 
  if (error) return <StatusChip msg={error} color='danger'>{error}</StatusChip>
  if (!intent.clientSecret || loading) return <div className=''>
    <div className='text-center text-chCaption mb-2'>Retrieving Payment Options</div>
    <div className='w-full flex justify-center'><Loading /></div>
  </div>

  const options = {
    clientSecret: intent.clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#fb7e00'
      }
    }
  }

  return <Elements stripe={stripePromise} options={options}>
    <PurchaseForm user={user} intent={intent} reset={reset} gettingIntent={gettingIntent} cb={cb} validate={validate} />
  </Elements>
}

const PurchaseForm = ({ user, intent, reset, gettingIntent, cb, validate }) => {
  const [error, setError] = useState('')
  const [processing, setProcessing] = useState('')
  const [fetchingIntent, setFetchingIntent] = useState(gettingIntent)
  const stripe = useStripe()
  const elements = useElements()
  
  useEffect(() => {
    setFetchingIntent(gettingIntent)
  }, [gettingIntent])

  const handleChange = (event) => {
    setError('')
  }

  const handleSubmit = async () => {
    
    if (validate) {
      const validationErrors = validate()
      if (validationErrors) {
        setError(validationErrors)
        return
      }
    }
    setProcessing(true)

    await elements.fetchUpdates()
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.protocol}//${window.location.host}/stripe/${intent.type}`,
        receipt_email: user.email
      },
      redirect: 'if_required'
    })
    
    setProcessing(false)
    if (result.error) {
      console.error(result.error)
      setError(`Payment failed: ${result.error.message}`)
      return
    }
      
    // result can include redirect to confirm payment (bancontact, ideal) or more likely
    // a payment intent with status
    if (result.paymentIntent && result.paymentIntent.id && result.paymentIntent.object === 'payment_intent' && result.paymentIntent.status === 'succeeded') {
      setError(null)
      logEvent(`purchase-${intent.type}`, intent.quantity)
      localStorage.removePaymentIntent(intent.type)
      if (cb) await cb(reset, result.paymentIntent)
    }
  }

  return <>
    <div className='flex justify-center'>
      <StatusChip color='danger' msg={error} /> 
    </div>
    {(intent.id) && <div className='flex justify-center items-center w-full'>
      <form id='payment-form' >
        <div className='w-auto md:w-[500px] p-4 border-1 border-primary rounded-md'>
          <PaymentElement onChange={handleChange}  />
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            color='primary'
            size='lg'
            type='submit'
            className='mt-4'
            isDisabled={processing || fetchingIntent} 
            onPress={() => {
              handleSubmit()
            }}>
            {fetchingIntent && <>Calculating Price</>}
            {!fetchingIntent && <>
              {processing ? 'PROCESSING ...' : (intent.type === 'entryFee' ? `Pay ${formatMoneyIntl(intent.amount / 100, intent.currency)}` : `Complete ${formatMoneyIntl(intent.amount / 100, intent.currency)}  ${intent.type === 'donation' ? 'Donation' : 'Purchase'}`)}
            </>}
          </Button>
        </div>
      </form>
    </div>}
  </>
}

export default Purchase
